import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ErrorAnalysisPanel = ({ deliveryLogs }) => {
  const [errorStats, setErrorStats] = useState(null);
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    analyzeErrors();
  }, [deliveryLogs]);

  const analyzeErrors = () => {
    const failedLogs = deliveryLogs?.filter(log => log?.notification_status === 'failed') || [];
    
    // Categorize errors
    const errorCategories = {
      smtp_errors: [],
      dns_resolution: [],
      recipient_validation: [],
      rate_limiting: [],
      authentication: [],
      other: []
    };

    failedLogs?.forEach(log => {
      const errorMsg = log?.error_message?.toLowerCase() || '';
      
      if (errorMsg?.includes('smtp') || errorMsg?.includes('mail server')) {
        errorCategories?.smtp_errors?.push(log);
      } else if (errorMsg?.includes('dns') || errorMsg?.includes('domain')) {
        errorCategories?.dns_resolution?.push(log);
      } else if (errorMsg?.includes('invalid') || errorMsg?.includes('recipient')) {
        errorCategories?.recipient_validation?.push(log);
      } else if (errorMsg?.includes('rate') || errorMsg?.includes('limit')) {
        errorCategories?.rate_limiting?.push(log);
      } else if (errorMsg?.includes('auth') || errorMsg?.includes('api key')) {
        errorCategories?.authentication?.push(log);
      } else {
        errorCategories?.other?.push(log);
      }
    });

    setErrorStats({
      total: failedLogs?.length,
      categories: errorCategories,
      recentErrors: failedLogs?.slice(0, 10)
    });
  };

  const handleRetry = async (logId) => {
    try {
      const { error } = await supabase
        ?.from('notification_queue')
        ?.update({ 
          notification_status: 'pending',
          retry_count: supabase?.raw('retry_count + 1')
        })
        ?.eq('id', logId);

      if (error) throw error;
      alert('Email queued for retry');
    } catch (error) {
      console.error('Error retrying email:', error);
      alert('Failed to retry email');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      smtp_errors: 'Server',
      dns_resolution: 'Globe',
      recipient_validation: 'UserX',
      rate_limiting: 'Clock',
      authentication: 'Lock',
      other: 'AlertCircle'
    };
    return icons?.[category] || 'AlertCircle';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      smtp_errors: 'SMTP Errors',
      dns_resolution: 'DNS Resolution Issues',
      recipient_validation: 'Recipient Validation Failures',
      rate_limiting: 'Rate Limiting Alerts',
      authentication: 'Authentication Errors',
      other: 'Other Errors'
    };
    return labels?.[category] || category;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="AlertTriangle" size={24} className="text-red-600 dark:text-red-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Error Analysis</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Comprehensive failure categorization with automated retry mechanisms</p>
        </div>
      </div>

      {/* Error Statistics */}
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertCircle" size={24} className="text-red-600 dark:text-red-400" />
          <div>
            <h4 className="text-lg font-semibold text-red-900 dark:text-red-200">Total Failures</h4>
            <p className="text-3xl font-bold text-red-600 dark:text-red-400">{errorStats?.total || 0}</p>
          </div>
        </div>
      </div>

      {/* Error Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {errorStats?.categories && Object.entries(errorStats?.categories)?.map(([category, logs]) => (
          <div
            key={category}
            className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <Icon name={getCategoryIcon(category)} size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {getCategoryLabel(category)}
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {logs?.length || 0}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Errors with Retry */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Recent Errors with Retry Capability</h4>
        <div className="space-y-3">
          {errorStats?.recentErrors?.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-slate-700 rounded-lg">
              <Icon name="CheckCircle" size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No errors found - all emails delivered successfully!</p>
            </div>
          ) : (
            errorStats?.recentErrors?.map((log) => (
              <div
                key={log?.id}
                className="bg-white dark:bg-slate-800 rounded-lg border border-red-200 dark:border-red-800 p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="XCircle" size={18} className="text-red-600 dark:text-red-400" />
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {log?.recipient_email}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Failed at: {new Date(log?.created_at)?.toLocaleString()}
                    </p>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded p-3 mb-3">
                      <p className="text-xs font-semibold text-red-900 dark:text-red-200 mb-1">Error Code & Details</p>
                      <p className="text-xs text-red-700 dark:text-red-300">
                        {log?.error_message || 'Unknown error'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                      <span>Retry Count: {log?.retry_count || 0}</span>
                      <span>Subject: {log?.subject || 'No subject'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRetry(log?.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Icon name="RefreshCw" size={16} />
                    Retry
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorAnalysisPanel;